<template>
  <module-card
    v-bind="params"
    :loading="loading"
    :permissions="permissions"
    :roles="roles"
    :error="error"
    :errorText="errorText"
    :retryFunction="retryFunction"
    :segmentations="segmentations"
    @visibleChanged="$emit('visibleChanged', $event)"
  >
    <template slot="content">
      <div class="payment-slip" v-if="!debitBlock">
        <div v-if="Array.isArray(items) && items.length > 0">
          <div>
            <p
              class="payment-slip__number"
              v-html="$t('pages.paymentSlip.toExpireSlipCount', [items.length])"
            />
            <v-carousel
              class="payment-slip__carousel"
              height="60px"
              delimiter-icon
              prev-icon="bexsicon-left"
              next-icon="bexsicon-right"
              :hide-controls="this.items.length === 1"
              :hide-delimiters="this.items.length === 1"
            >
              <v-carousel-item v-for="(item, i) in items" :key="i">
                <div class="payment-slip__info">
                  <span class="payment-slip__data">
                    {{ formatDate(item.expirationDate) }} |
                    {{ formatMoney(item.discountedValue) }}
                    <v-progress-circular
                      color="green"
                      indeterminate
                      size="15"
                      :width="3"
                      v-if="!item.discountedValue"
                    ></v-progress-circular>
                  </span>
                  <PaymentButton
                    :item="item"
                    :email="email"
                    :cellphone="cellphone"
                    :buttonClass="'payment-slip__pay-btn financial--background'"
                    @openSlip="$emit('paySlip', item)"
                    :sendByEmail="sendSlipByEmail"
                    :sendBySms="sendSlipBySms"
                  />
                </div>
              </v-carousel-item>
            </v-carousel>
          </div>
          <router-link
            tag="button"
            :to="{ name: 'toExpirePaymentSlip' }"
            class="payment-slip__button btn btn-primary py-0"
          >
            <i
              class="bexsicon-payment-sm btn-icon payment-slip__button__icon"
            ></i>
            <span>{{ $t('pages.paymentSlip.showPaymentSlips') }}</span>
          </router-link>
        </div>
        <div v-else-if="items === null">
          <div class="payment-slip__clean">
            <v-progress-circular
              indeterminate
              class="payment-slip__loader"
            ></v-progress-circular>
          </div>
        </div>
        <div v-else>
          <div class="payment-slip__clean">
            <i class="bexsicon-payment payment-slip__clean__icon"></i>
            <span
              class="payment-slip__clean__message"
              v-html="emptyPaymentSlipsHtml"
            />
          </div>
        </div>
        <div class="payment-slip__footer__container">
          <div v-if="hasLateSlips">
            <div class="payment-slip__footer">
              <span>{{ $t('pages.paymentSlip.lateSlipsSubtitle') }}</span>
            </div>
            <div class="payment-slip__late">
              <div class="payment-slip__late__icon">
                <i class="bexsicon-alert icon"></i>
              </div>
              <router-link
                v-if="hasPayableLateSlips"
                tag="div"
                :to="{
                  name: 'latePaymentSlip',
                }"
                class="payment-slip__late__info"
              >
                <span>
                  {{
                    hasLateReadySolicitations
                      ? $t('pages.paymentSlip.lateSlipReady')
                      : $t('pages.paymentSlip.payableLateMessage')
                  }}
                  <span style="text-decoration: underline">
                    {{ $t('pages.paymentSlip.clickHereAppend') }}
                  </span>
                </span>
              </router-link>
              <router-link
                v-else
                tag="div"
                :to="{
                  name: 'ticketForm',
                }"
                class="payment-slip__late__info"
              >
                <span>
                  {{ $t('pages.paymentSlip.notPayableLateMessage') }}
                  <span
                    v-if="ticketCrmEnabled"
                    style="text-decoration: underline"
                  >
                    {{ $t('pages.paymentSlip.supportLink') }}
                  </span>
                </span>
              </router-link>
            </div>
          </div>

          <div v-else-if="hasAnticipationReadySolicitations">
            <div class="payment-slip__footer">
              <span>{{
                $t('pages.paymentSlip.anticipationSlipsSubtitle')
              }}</span>
            </div>
            <div class="payment-slip__footer">
              <span style="color: rgb(75, 182, 41)">
                {{ $t('pages.paymentSlip.anticipationSlipReady') }}
                <router-link
                  tag="div"
                  :to="{ name: 'anticipationPaymentSlip' }"
                >
                  <span class="payment-slip__link">
                    <b>{{ $t('pages.paymentSlip.clickHereAppend') }}</b>
                  </span>
                </router-link>
              </span>
            </div>
          </div>

          <div v-else>
            <div class="payment-slip__footer">
              <span>{{ $t('pages.paymentSlip.lateSlipsSubtitle') }}</span>
            </div>
            <div class="payment-slip__fine">
              <span
                class="payment-slip__fine__message"
                v-html="$t('pages.paymentSlip.emptyLateSlips')"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="payment-slip" v-else>
        <div class="payment-slip__blocked">
          <div class="payment-slip__blocked__container">
            <i class="bexsicon-alert payment-slip__blocked__icon"></i>
            <div class="payment-slip__blocked__info">
              <span>
                <b>{{ $t('pages.paymentSlip.operationNotAllowed') }}</b>
              </span>
              <br />
              <span>{{
                $t('pages.paymentSlip.partialSlipWarningSubTitle')
              }}</span>
              <router-link
                v-if="ticketCrmEnabled"
                tag="p"
                :to="{ name: 'ticketForm' }"
                class="payment-slip-link"
                ><u>{{ $t('pages.paymentSlip.supportLink') }}</u>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </template>
  </module-card>
</template>

<script>
import bus from '@/utils/bus';
import {
  PAYMENT_SLIPS,
  PROFILES,
  ROLES,
  SEGMENTATIONS,
  TICKET_CRM,
  USER,
} from '../../utils/constants';
import ModuleCard from '@/components/layout/Card';
import PaymentButton from '@/components/financial/PaymentButton';
export default {
  components: {
    ModuleCard,
    PaymentButton,
  },
  data() {
    return {
      email: '',
      cellphoneDefault: {
        countryCode: '+55',
        areaCode: '11',
        number: '',
      },
      permissions: [
        PROFILES.ADMIN_PROFILE,
        PROFILES.ADMIN_BEXS_PROFILE,
        PROFILES.ADMIN_HR_PROFILE,
        PROFILES.HR_PROFILE,
        PROFILES.FINANCIAL_PROFILE,
        PROFILES.ADMIN_FINANCIAL_PROFILE,
      ],
      roles: [ROLES.CLIENT_ROLE],
      segmentations: [
        SEGMENTATIONS.ASSOCIATIVE__REHEARSE_STATUS,
        SEGMENTATIONS.ASSOCIATIVE_REHEARSED__REHEARSE_STATUS,
        SEGMENTATIONS.SFH__REHEARSE_STATUS,
        SEGMENTATIONS.DIRECT__REHEARSE_STATUS,
        SEGMENTATIONS.LAUNCH__CONSTRUCTION_STAGE,
        SEGMENTATIONS.IN_CONSTRUCTION__CONSTRUCTION_STAGE,
        SEGMENTATIONS.DELIVERED__CONSTRUCTION_STAGE,
        SEGMENTATIONS.KEYS_DELIVERED__CONSTRUCTION_STAGE,
      ],
    };
  },
  computed: {
    toExpireSlips() {
      return this.$store.getters[PAYMENT_SLIPS.GET_TO_EXPIRE];
    },
    ticketCrmEnabled() {
      return this.$store.getters[TICKET_CRM.GET_ENABLED];
    },
    cellphone() {
      const imobmeUser = this.$store.getters[USER.GET_IMOBME_DATA];

      const { number, areaCode, countryCode } =
        imobmeUser && imobmeUser.cellphone
          ? imobmeUser.cellphone
          : this.cellphoneDefault;

      const cellphone = { number, areaCode, countryCode: countryCode || '+55' };

      return cellphone;
    },
    emptyPaymentSlipsHtml() {
      return this.$t('pages.paymentSlip.emptyToExpireSlips', {
        extraInfo: this.hasFuturePaymentSlips
          ? this.$t('pages.paymentSlip.hasFutureToExpireSlips')
          : '',
      });
    },
  },
  created() {
    this.email = this.user.email;

    bus.$on('toExpireSlip:requestReady', this.update);
  },
  beforeDestroy() {
    bus.$off('toExpireSlip:requestReady', this.update);
  },
  methods: {
    update() {
      this.$forceUpdate();
    },
  },
  props: {
    items: {
      type: Array | null,
      required: true,
    },
    hasLateSlips: {
      type: Boolean,
      required: true,
    },
    hasPayableLateSlips: {
      type: Boolean,
      required: true,
    },
    hasFuturePaymentSlips: {
      type: Boolean,
      required: true,
    },
    debitBlock: {
      type: Boolean,
      default: false,
    },
    params: {
      type: Object,
      required: true,
    },
    error: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    errorText: {
      type: String,
      required: true,
    },
    retryFunction: {
      type: Function,
      required: true,
    },
    hasAnticipationReadySolicitations: {
      type: Boolean,
      default: false,
    },
    hasLateReadySolicitations: {
      type: Boolean,
      default: false,
    },
    sendSlipByEmail: {
      type: Function,
      required: true,
    },
    sendSlipBySms: {
      type: Function,
      required: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/breakpoint';

>>> .v-card__text {
  padding-top: 5px;
}

.payment-slip {
  &__carousel {
    height: 30px;
    box-shadow: none;

    >>> .v-carousel__controls {
      background-color: transparent;
      height: 10px;
      margin: 5px;
      .v-btn {
        background-color: lightgray;
        border-radius: 50% !important;
        width: 10px !important;
        height: 10px !important;
        padding: 0 !important;
      }

      &__item--active {
        background-color: var(--financial-color) !important;
      }
    }

    >>> .v-carousel__next {
      right: 0;
    }

    >>> .v-carousel__prev {
      left: 0;
    }

    >>> .v-carousel__next,
    >>> .v-carousel__prev {
      top: 55% !important;

      .v-btn {
        color: gray;
        width: 15px;
        height: 25px;
        padding: 0 !important;

        i {
          font-size: 20px !important;
        }
      }

      &__item--active {
        background-color: var(--financial-color) !important;
      }
    }

    >>> .v-carousel__item {
      margin: auto;
      padding: 10px 5px 5px;
      // top: -35px;
    }
  }

  &__loader {
    // margin: 20% 0;
    // height: 50%;
    top: 16px;
    // left: 50%;
    color: var(--brand-color);
    width: 100% !important;
  }

  &__number {
    position: relative;
    padding-top: 0px;
    padding-bottom: 0px;
    text-align: center;
    margin: 0px;
    font-size: 0.75rem;
  }

  &__info {
    display: flex;
    margin-top: 10px;
    margin-bottom: 10px;
    margin-left: 13px;
    margin-right: 13px;
  }

  &__data {
    margin: auto;
    font-size: 12px;
    flex: 0.8;
  }

  &__pay-btn {
    flex: 0.2;
    z-index: 1;
    height: 25px;
    min-width: 60px;
    font-size: 12px;
    margin: 0;
    padding: 5px 14px !important;
    border-radius: 5px;
    text-decoration: none;
    color: #fff;
    text-align: center;
    line-height: 25px;
  }

  &__footer {
    margin-top: 5px;
    margin-bottom: 5px;
    font-size: 11px;

    &__container {
      margin-top: 7px;
      border-top: 1px solid #f0f0f0;
    }
  }

  &__clean {
    margin: 16px 0;
    background: var(--card-backdrop-color);
    border-radius: 2px;
    min-height: 5rem;
    min-width: 10rem;
    display: flex;

    &__icon {
      color: var(--grey-icon-color);
      font-size: 2.5rem;
      background: #fff;
      border-radius: 50px;
      padding: 10px;
      margin-top: auto;
      margin-bottom: auto;
      margin-left: 1.2rem;
    }

    &__message {
      margin-bottom: auto;
      margin-top: auto;
      margin-left: 0.8rem;
      color: var(--label-color);
    }
  }

  &__fine {
    background: var(--card-backdrop-color);
    border-radius: 2px;
    min-height: 3rem;
    min-width: 10rem;
    display: flex;

    &__message {
      margin-bottom: auto;
      margin-top: auto;
      margin-left: auto;
      margin-right: auto;
      color: var(--financial-color);
    }
  }

  &__late {
    background: var(--error-color);
    border-radius: 4px;
    color: white !important;
    display: flex;
    padding-top: 4px;
    padding-bottom: 4px;
    cursor: pointer;

    &__info {
      font-size: 11px;
      flex: 0.9;
      align-self: center;
      margin-right: 2px;

      span {
        font-weight: bold;
      }
    }

    &__icon {
      margin-top: 3px;
      margin-bottom: auto;
      margin-left: 7px;
      padding: 0;
      flex: 0.08;
    }
  }

  &__blocked {
    display: block;
    height: 100%;
    background: #f1f1f1;
    border-radius: 4px;
    color: var(--error-color) !important;

    &__container {
      display: flex;
      padding: 20px;
    }

    &__info {
      font-size: 11px;
      flex: 0.85;
      margin: auto 5px;
    }

    &__icon {
      flex: 0.1;
      color: var(--grey-icon-color);
      font-size: 2.5rem;
      background: #fff;
      border-radius: 50px;
      padding: 10px;
      margin-top: auto;
      margin-bottom: auto;
      margin-left: 1.2rem;
      margin-right: 0.5rem;
    }
  }

  &__button {
    background-color: #f1f1f1;
    border: none;
    color: var(--financial-color);
    font-weight: 500;
    font-size: 12px;
    padding: 4px !important;
    display: flex;
    border-radius: 4px;

    &__icon {
      color: var(--financial-color);
      margin-right: 3px;
      margin-top: auto;
      margin-bottom: auto;
      font-size: 1.1rem;
      padding-left: 4px;
    }

    &:hover {
      background-color: var(--financial-color);
      color: #fff;

      .payment-slip__button__icon {
        color: #fff;
      }
    }
  }

  &__link {
    cursor: pointer;
    font-weight: bold;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/modules/PaymentSlipCard.vue